import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2BCatalogApi from 'api/b2b/CatalogApiB2b'
import TenantCatalogApi, {
  ITenantCatalogBackendData
} from 'api/b2b/TenantCatalog'
import OrbitSession from 'hooks/useCookies'

import CatalogGroupList from './CatalogGroupList'

interface IAutoCompleteDropdown {
  label: string
  value: string
}

interface IFormInput {
  companyName: string
  currency: string
  products: IAutoCompleteDropdown
}

function CatalogDiscount() {
  const [searchParams] = useSearchParams()

  const companyId = searchParams.get('id') || ''
  const userEmail = OrbitSession.get('email')
  const token = OrbitSession.get('faberToken')

  const [selectedRowId, setSelectedRowId] = useState<string[]>([])
  const [discountValues, setDiscountValues] = useState<Record<string, number>>(
    {}
  )

  // mutation for fetching the existing catalog group data
  const { data: catalogGroupData } = useQuery(
    ['specific-catalog-group-details', companyId],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await TenantCatalogApi.getCatalogGroupById(
        companyId,
        token
      )
      return data
    },
    {
      enabled: !!companyId,
      refetchOnMount: true,
      onError: () => {
        toast.error('Error fetching catalog group details')
      }
    }
  )

  const {
    // status,
    data: catalogGroupListData,
    isLoading: isCatalogGroupListLoading,
    isError: catalogGroupFetchListError,
    refetch: refetchCatalogGroupList
  } = useQuery(['catalog-group'], async () => {
    const token = OrbitSession.get('faberToken')
    const { data } = await B2BCatalogApi.getCatalogGroup(token)
    return data
  })

  const { control, handleSubmit, setValue, watch } = useForm<IFormInput>({
    defaultValues: {
      companyName: '',
      currency: '',
      products: { value: 'esim', label: 'eSIM' }
    }
  })

  const watchedProducts = watch('products')

  const validateSubmission = (): string | null => {
    if (!selectedRowId) {
      return 'Please select a catalog group'
    }
    if (!companyId) {
      return 'Tenant information is missing'
    }
    if (!userEmail) {
      return 'User email is missing'
    }
    if (!watchedProducts?.value) {
      return 'Please select a product type'
    }
    return null
  }
  // Mutation to update the
  const { mutate: updateTenantCatalog, isLoading: isUpdatingData } =
    useMutation(
      (data: ITenantCatalogBackendData) =>
        TenantCatalogApi.updateTenantCatalog(data, token),
      {
        onSuccess: () => {
          toast.success('Catalog updated successfully', {
            hideProgressBar: false,
            autoClose: 2000
          })
        },
        onError: (error) => {
          console.error('API Error:', error)
          toast.error('Failed to create catalog')
        }
      }
    )

  const onSubmit: SubmitHandler<IFormInput> = (formData) => {
    const validationError = validateSubmission()

    // extra check to make sure the company id is not empty
    if (!companyId || !catalogGroupData) {
      toast.error('Please select a company first from TDP Details list')
      return
    }

    if (validationError) {
      toast.error(validationError)
      return
    }

    const catalog_group_payload = selectedRowId?.map((id) => ({
      catalog_group_id: id,
      discount_percentage: discountValues[id]?.toString() || '0'
    }))

    const submitData: ITenantCatalogBackendData = {
      tenant_id: companyId,
      product_type: formData.products.value,
      updated_by: userEmail,
      tenant_catalog_group_discount: catalog_group_payload
    }
    updateTenantCatalog(submitData)
  }

  // Set the values when catalogGroupData is fetched
  useEffect(() => {
    if (catalogGroupData) {
      setValue('companyName', catalogGroupData.company_name)
      setValue('currency', catalogGroupData.currency)
      setSelectedRowId(() =>
        catalogGroupData.tenant_catalog_group_discount.map((item) =>
          String(item.catalog_group_id)
        )
      )
      setDiscountValues(() => {
        return catalogGroupData.tenant_catalog_group_discount.reduce(
          (acc, item) => {
            acc[item.catalog_group_id] = item.discount_percentage
            return acc
          },
          {} as Record<string, number>
        )
      })
    }
  }, [catalogGroupData, setValue])

  if (catalogGroupFetchListError) {
    return (
      <Box>
        <Typography color='error'>Error loading data</Typography>
        <Button onClick={() => refetchCatalogGroupList()}>Retry</Button>
      </Box>
    )
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>
            Company Name
          </Typography>
          <Controller
            name='companyName'
            control={control}
            render={({ field }) => (
              <TextField
                id='companyName'
                variant='outlined'
                size='small'
                disabled={true}
                sx={{ width: '300px' }}
                {...field}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>Currency</Typography>
          <Controller
            name='currency'
            control={control}
            render={({ field }) => (
              <TextField
                id='currency'
                variant='outlined'
                size='small'
                disabled={true}
                sx={{ width: '100px' }}
                {...field}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center' sx={{ mb: 3 }}>
          <Typography sx={{ mr: 2, minWidth: '120px' }}>Map Product</Typography>
          <Controller
            name='products'
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                disableClearable
                options={[{ value: 'esim', label: 'eSIM' }]}
                onChange={(_, data) => field.onChange(data)}
                size='small'
                sx={{ width: '300px' }}
                renderInput={(params) => <TextField {...params} />}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <CatalogGroupList
            data={catalogGroupListData || []}
            isLoading={isCatalogGroupListLoading}
            setSelectedRowId={setSelectedRowId}
            setDiscountValues={setDiscountValues}
            selectedRowId={selectedRowId}
            discountValues={discountValues}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            type='submit'
            variant='contained'
            disableElevation
            disabled={
              !selectedRowId || isCatalogGroupListLoading || isUpdatingData
            }
          >
            {isUpdatingData ? <CircularProgress /> : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default CatalogDiscount
